/**
 * Native dockview-tab operations for a *tabbed area*, used to host multiple
 * *view instances* (§5.7 视图多实例) as real dockview tabs — AI chat sessions in
 * the `auxiliaryBar`, one-document-per-tab in the `editor` (见 20 二期).
 * Module-agnostic: everything is keyed by a `viewId` + `instanceKey`, never by
 * any domain concept.
 *
 * The area keeps a structural *anchor* panel (`id === area`) so its group and
 * the visibility / sizing machinery in `layout-builder` stay alive even with no
 * open instances; instance tabs are added as siblings of that anchor. The
 * anchor's own tab is collapsed via the `areaAnchor` tab component + CSS
 * (`:has(.app-anchor-tab)`: 0-width and `sticky` / `left: 0` on the strip so
 * overflow math skips it, `display: none` on overflow clones), so the strip
 * *and* the overflow dropdown show only instance tabs.
 *
 * The area is a **parameter**, not a constant: the exact same mechanism serves
 * both areas, and hard-coding one is what would force a second implementation
 * for the other.
 */
import type { LayoutArea } from '@hibro/shared';
import type { DockviewApi, DockviewPanelRenderer } from 'dockview-react';

import { makeInstanceId } from '../editor/editor-tabs';
import { isAreaVisibleNow, setAreaVisible } from './layout-builder';

/**
 * Areas that can host view-instance tabs (§5.7).
 *
 * `editor` holds document-like instances (one tab per document, 见 20); the
 * `auxiliaryBar` holds side-panel instances (AI sessions). A `LayoutArea` outside
 * this set has no anchor tab component and would render a bare dockview strip, so
 * `open` refuses rather than producing broken chrome.
 */
export const TABBED_AREAS: readonly LayoutArea[] = ['auxiliaryBar', 'editor'];

/**
 * The side-panel tabbed area (§5.7). Still exported by name because the
 * auxiliaryBar is the area whose *visibility* is coupled to having tabs at all
 * (opening one reveals it, closing the last hides it — see `AiAuxTabController`).
 */
export const TABBED_AREA: LayoutArea = 'auxiliaryBar';

/** Whether `area` may host view-instance tabs (§5.7). */
export function isTabbedArea(area: LayoutArea): boolean {
  return TABBED_AREAS.includes(area);
}

export interface OpenAuxTabOptions {
  viewId: string;
  moduleId?: string;
  instanceKey?: string;
  title: string;
  /** Focus the tab after opening. Default true. */
  activate?: boolean;
  /**
   * How dockview renders the tab while it is not the group's active panel
   * (§5.7 视图缓存). Derived from the view's cache policy by the caller
   * (`resolveTabRenderer`); defaults to dockview's `onlyWhenVisible`.
   */
  renderer?: DockviewPanelRenderer;
}

/**
 * Open (or focus) a view-instance tab in `area`. Existing instance → focus it;
 * otherwise ensure the area is visible, then add a `surfaceTab` panel as a
 * sibling of the area anchor.
 *
 * Open-**or**-focus is the whole point for links (见 20): the instance id *is* the
 * identity, so navigating to the same document twice raises the tab already open
 * instead of stacking a second one.
 */
export function openAuxTab(
  api: DockviewApi,
  opts: OpenAuxTabOptions,
  area: LayoutArea = TABBED_AREA,
): void {
  const id = makeInstanceId(opts.viewId, opts.instanceKey);
  const existing = api.getPanel(id);
  if (existing) {
    if (opts.activate !== false) {
      existing.api.setActive();
    }
    return;
  }
  if (!isAreaVisibleNow(api, area)) {
    setAreaVisible(api, area, true);
  }
  const anchor = api.getPanel(area);
  if (!anchor) {
    return;
  }
  api.addPanel({
    id,
    component: 'surfaceTab',
    // Custom tab header (inline rename + themed close, §5.7); see `SurfaceTab`.
    tabComponent: 'surfaceTab',
    title: opts.title,
    params: {
      viewId: opts.viewId,
      moduleId: opts.moduleId,
      instanceKey: opts.instanceKey,
      title: opts.title,
    },
    position: { referenceGroup: anchor.group.id },
    renderer: opts.renderer ?? 'onlyWhenVisible',
    inactive: opts.activate === false,
  });
}
